import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Video, Camera, Image as ImageIcon, X, Square, Loader2, RotateCcw, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Three-button video picker:
 *   a) In-App Recorder — opens a live preview + record button inside the PWA
 *   b) Use My Camera   — opens native camera (capture="environment")
 *   c) Choose from Gallery — opens native file picker (no capture)
 *
 * Props:
 *   onFileSelected(file: File): called once the user has a final video file
 *   disabled?: boolean
 *   compact?: boolean (smaller buttons)
 */
export default function VideoSourcePicker({ onFileSelected, disabled = false, compact = false }) {
  const cameraRef = useRef(null);   // native camera capture
  const galleryRef = useRef(null);  // gallery picker
  const [showRecorder, setShowRecorder] = useState(false);

  const handleNativeFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onFileSelected(file);
    // reset so re-selecting the same file fires onChange
    if (e.target) e.target.value = '';
  };

  const btnBase = compact
    ? 'flex-1 flex flex-col items-center justify-center gap-1 py-2.5 px-2 rounded-lg border text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-40'
    : 'flex-1 flex flex-col items-center justify-center gap-1.5 py-4 px-3 rounded-xl border-2 text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-40';

  return (
    <>
      <div className="flex items-stretch gap-2" data-testid="video-source-picker">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setShowRecorder(true)}
          className={`${btnBase} bg-[#2563EB] text-white border-[#2563EB] hover:bg-[#1d4ed8] hover:border-[#1d4ed8]`}
          data-testid="video-record-in-app-btn"
        >
          <Video size={compact ? 16 : 22} />
          <span>Record In App</span>
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => cameraRef.current?.click()}
          className={`${btnBase} bg-white text-[#0A2242] border-[#E2E8F0] hover:border-[#2563EB] hover:text-[#2563EB]`}
          data-testid="video-camera-btn"
        >
          <Camera size={compact ? 16 : 22} />
          <span>Camera</span>
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => galleryRef.current?.click()}
          className={`${btnBase} bg-white text-[#0A2242] border-[#E2E8F0] hover:border-[#2563EB] hover:text-[#2563EB]`}
          data-testid="video-gallery-btn"
        >
          <ImageIcon size={compact ? 16 : 22} />
          <span>Gallery</span>
        </button>

        {/* Hidden native inputs */}
        <input
          ref={cameraRef}
          type="file"
          accept="video/*"
          capture="environment"
          onChange={handleNativeFile}
          className="hidden"
          data-testid="video-camera-input"
        />
        <input
          ref={galleryRef}
          type="file"
          accept="video/*"
          onChange={handleNativeFile}
          className="hidden"
          data-testid="video-gallery-input"
        />
      </div>

      <AnimatePresence>
        {showRecorder && (
          <InAppRecorder
            onClose={() => setShowRecorder(false)}
            onCapture={(file) => {
              setShowRecorder(false);
              onFileSelected(file);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

/* ---------------- In-App Recorder Modal ---------------- */

function InAppRecorder({ onClose, onCapture }) {
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startTimeRef = useRef(0);
  const tickRef = useRef(null);

  const [stage, setStage] = useState('init'); // 'init' | 'ready' | 'recording' | 'preview' | 'error'
  const [elapsed, setElapsed] = useState(0);  // seconds
  const [errorMsg, setErrorMsg] = useState('');
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);

  const MAX_SECONDS = 60; // hard cap, mirrors typical drill clip length

  useEffect(() => {
    startCamera();
    return () => {
      stopAllTracks();
      if (tickRef.current) clearInterval(tickRef.current);
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.play().catch(() => { /* user gesture pending */ });
      }
      setStage('ready');
    } catch (err) {
      setErrorMsg(
        err?.name === 'NotAllowedError'
          ? 'Camera & mic permission denied. Open device Settings → enable camera and microphone for this site, then try again.'
          : err?.message || 'Could not access camera. Try the Camera or Gallery option instead.'
      );
      setStage('error');
    }
  };

  const stopAllTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const pickMimeType = () => {
    const candidates = [
      'video/mp4',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
    ];
    for (const m of candidates) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(m)) return m;
    }
    return '';
  };

  const handleStart = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mime = pickMimeType();
    let recorder;
    try {
      recorder = mime ? new MediaRecorder(streamRef.current, { mimeType: mime }) : new MediaRecorder(streamRef.current);
    } catch (err) {
      toast.error('Recorder not supported on this browser. Use Camera button instead.');
      return;
    }
    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime || 'video/webm' });
      const url = URL.createObjectURL(blob);
      setRecordedBlob(blob);
      setRecordedUrl(url);
      setStage('preview');
      stopAllTracks();
    };
    recorder.start(1000);
    startTimeRef.current = Date.now();
    setElapsed(0);
    setStage('recording');
    tickRef.current = setInterval(() => {
      const secs = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsed(secs);
      if (secs >= MAX_SECONDS) {
        handleStop();
      }
    }, 250);
  };

  const handleStop = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };

  const handleRetake = async () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setElapsed(0);
    setStage('init');
    await startCamera();
  };

  const handleUse = () => {
    if (!recordedBlob) return;
    const ext = recordedBlob.type.includes('mp4') ? 'mp4' : 'webm';
    const file = new File([recordedBlob], `recording-${Date.now()}.${ext}`, { type: recordedBlob.type });
    onCapture(file);
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(1, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black flex flex-col"
      data-testid="in-app-recorder"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/70 text-white safe-top">
        <button onClick={onClose} className="text-white/80 hover:text-white" aria-label="Close" data-testid="recorder-close-btn">
          <X size={22} />
        </button>
        <span className="text-xs font-bold uppercase tracking-widest">
          {stage === 'recording' ? (
            <span className="flex items-center gap-1.5 text-[#EF4444]">
              <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" /> REC {fmt(elapsed)}
            </span>
          ) : stage === 'preview' ? 'PREVIEW' : 'IN-APP RECORDER'}
        </span>
        <span className="text-[10px] text-white/60 tabular-nums">{fmt(MAX_SECONDS)} max</span>
      </div>

      {/* Stage area */}
      <div className="flex-1 relative bg-black flex items-center justify-center overflow-hidden">
        {stage === 'error' ? (
          <div className="px-6 text-center text-white">
            <p className="text-sm mb-3">{errorMsg}</p>
            <button onClick={onClose} className="text-[#00E5FF] underline text-xs font-bold uppercase tracking-widest" data-testid="recorder-error-close">
              Close
            </button>
          </div>
        ) : stage === 'preview' && recordedUrl ? (
          <video
            ref={previewRef}
            src={recordedUrl}
            controls
            playsInline
            className="max-w-full max-h-full"
            data-testid="recorder-preview-video"
          />
        ) : (
          <video
            ref={videoRef}
            playsInline
            muted
            className="max-w-full max-h-full"
            data-testid="recorder-live-video"
          />
        )}
      </div>

      {/* Control bar */}
      <div className="bg-black/80 backdrop-blur px-6 py-5 safe-bottom">
        {stage === 'ready' && (
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={handleStart}
              className="w-16 h-16 rounded-full bg-[#EF4444] hover:bg-[#dc2626] border-4 border-white shadow-2xl transition-colors"
              aria-label="Start recording"
              data-testid="recorder-record-btn"
            />
            <p className="text-[10px] text-white/60 uppercase tracking-widest">Tap to record</p>
          </div>
        )}
        {stage === 'recording' && (
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={handleStop}
              className="w-16 h-16 rounded-full bg-white hover:bg-white/90 flex items-center justify-center shadow-2xl transition-colors"
              aria-label="Stop recording"
              data-testid="recorder-stop-btn"
            >
              <Square size={22} className="text-[#EF4444]" fill="currentColor" />
            </button>
            <p className="text-[10px] text-white/60 uppercase tracking-widest">Tap to stop</p>
          </div>
        )}
        {stage === 'preview' && (
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={handleRetake}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold uppercase tracking-wide rounded-lg px-5 py-3 text-sm transition-colors"
              data-testid="recorder-retake-btn"
            >
              <RotateCcw size={16} /> Retake
            </button>
            <button
              onClick={handleUse}
              className="flex items-center gap-2 bg-[#10B981] hover:bg-[#0e9d6e] text-white font-bold uppercase tracking-wide rounded-lg px-5 py-3 text-sm transition-colors"
              data-testid="recorder-use-btn"
            >
              <CheckCircle2 size={16} /> Use This Video
            </button>
          </div>
        )}
        {stage === 'init' && (
          <div className="flex items-center justify-center gap-2 text-white text-xs">
            <Loader2 size={14} className="animate-spin" /> Starting camera…
          </div>
        )}
      </div>
    </motion.div>
  );
}
